'use client';

import { useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'motion/react';
import type { Variants } from 'motion/react';
import {
  RiCheckLine,
  RiCursorFill,
  RiEqualizerLine,
  RiFileList3Line,
  RiImageLine,
  RiKey2Line,
  RiRobot2Line,
  RiTimeLine,
} from '@remixicon/react';
import type { RemixiconComponentType } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { dur, ease, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

/**
 * "Kontrol sende" — panelin parayı harcarken uyduğu kuralların bölümü.
 *
 * Sayfadaki yeri: "Ne yapıyor" bölümünün hemen ALTI. O bölüm kullanıcıya
 * yazılımın ne yapabildiğini anlatıyor; tam o noktada "yani benim paramı bu
 * harcayacak" tedirginliği doğuyor. Burası onu karşılıyor.
 *
 * ── Bölümün iki parçası ───────────────────────────────────────────────────
 *
 * 1. **İki mod** — onay modu ile autopilot, İKİ SEKME. Üstte ortalanmış bir
 *    hap anahtar (fiyatlandırmadaki ödeme dönemi anahtarının derisi), altında
 *    ikiye bölünmüş bir panel: solda o modun metni ve üç adımlık akışı, sağda
 *    akışın kendini oynattığı canlı sahne (`ControlModes`).
 * 2. **Sınırlar · Denetim kaydı · Erişim** — tek bir sekmeli kart. Sekme
 *    şeridi üstte, kart altında ve kartın içi ikiye bölünmüş: solda metin,
 *    sağda ürünün kendi ekran görüntüsü (`ControlTabs`, dosyanın altında).
 *
 * Sekme örüntüsü tek bir yerde: `TabStrip`. İki blok da onu kullanıyor,
 * yalnızca `variant` farklı — deri değişiyor, klavye ve ARIA değişmiyor.
 *
 * ── Neden iddia değil mekanizma ───────────────────────────────────────────
 *
 * Rakiplerin sayfasında bu bölümün karşılığı yok, dolayısıyla ayırt edici
 * yer burası. "Kontrol sizde" cümlesi tek başına hiçbir şey ifade etmiyor —
 * herkes yazıyor. O yüzden söylenen her şeyin arkasında bir MEKANİZMA var:
 * onay kartının kendisi, sekmelerdeki üç ekran görüntüsü.
 *
 * 🔴 Üç sekmenin altında eskiden elle çizilmiş üç maket duruyordu (ölçer
 * şeritleri, denetim kaydı tablosu, OAuth izin ekranı). 12 Ağu 2026'da
 * (kullanıcı kararı) kaldırıldı: uydurulmuş üç maket üç ayrı görsel dil
 * demekti, ürünün kendi arayüzü hepsinden çok şey anlatıyor. "Ne yapar"
 * bölümü de aynı kararla tek görsele indi.
 *
 * ── İki modun farkı neden tek bakışta okunuyor ────────────────────────────
 *
 * Sahnedeki kart sekme değişince YENİDEN KURULMUYOR: aynı öneri kartı
 * yerinde kalıyor, yalnızca üstündeki anahtar kayıyor ve kartın SON SATIRI
 * değişiyor. Karşılaştırmayı mümkün kılan şey bu süreklilik — iki ayrı kart
 * basılsaydı göz önce ikisini eşitlemeye çalışır, farkı sonra görürdü.
 *
 * Önceki sürümde iki kart yan yanaydı. Sekmeye geçildi (kullanıcı kararı,
 * 12 Ağu 2026): yan yana iki durağan maket, tek bir OYNAYAN maketin
 * anlattığını anlatamıyor — modun farkı bir görüntü değil, bir akış.
 *
 * ── Erişilebilirlik ───────────────────────────────────────────────────────
 *
 * Maketlerdeki "Onayla / Reddet" gibi öğeler <span>; <button> DEĞİL. Hiçbiri
 * bir şey yapmıyor — odaklanılabilir olsalardı klavyeyle gezen kullanıcı beş
 * ölü durağa takılırdı. Anlamı taşıyan metin zaten h3/h4 ve paragraflarda.
 *
 * Sonsuz döngü yok: hiçbir şey kendi kendine tekrar etmiyor, dolayısıyla
 * `useReducedMotion` ile elle kapatılacak bir hareket de yok — kök
 * layout'taki <MotionConfig reducedMotion="user"> tamamını karşılıyor
 * (DESIGN.md §1). CSS geçişleri ayrı sistem, onlar globals.css'te
 * susturuluyor.
 */

/* Bloklar giriş hareketi taşımıyor: sayfa açılır açılmaz son hâllerindeler
   (`initial={false}`). Eskiden her blok kendi görüş alanı tetiğini taşıyordu;
   kaydırdıkça açılan sayfa 9 Eyl 2026'da kullanıcı kararıyla kaldırıldı.
   Variant'lar duruyor — sekmeye bağlı sahne hareketi onlardan bağımsız ve
   hâlâ `useInView` ile bölüme gelince başlıyor (aşağıda, ControlModes). */
const blockV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

const copyV: Variants = {
  hidden: { opacity: 0, y: 10, filter: 'blur(5px)' },
  show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: springFade(spring.default) },
};

/* Koyu yüzey ağır bir nesne: `soft` yayı (mass 1.1) onu kartondan değil
   metalden yapıyor — DESIGN.md §4 "ağırlık anlam taşır". */
const surfaceV: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: springFade(spring.soft, dur.base) },
};

/* Yüzeyin İÇİ: küçük parçalar, blur yok (§6 bütçesi) ve yol kısa. */
const partsV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight, delayChildren: 0.12 } },
};

const partV: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: { duration: dur.fast, ease: ease.out } },
};

export function Control() {
  const t = useTranslations('control');
  const uid = useId();

  return (
    <section className="control" aria-labelledby={`${uid}-title`}>
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <div className="control-inner">
            {/* ---------- Bölüm başlığı ---------- */}
            <motion.div className="control-head" variants={blockV} initial={false} animate="show">
              <motion.p className="control-eyebrow" variants={copyV}>
                {t('eyebrow')}
              </motion.p>
              <motion.h2 className="control-title" id={`${uid}-title`} variants={copyV}>
                {t.rich('title', { hl })}
              </motion.h2>
              <motion.p className="control-lead" variants={copyV}>
                {t('lead')}
              </motion.p>
            </motion.div>

            {/* ---------- 1) İki mod ---------- */}
            <motion.div className="control-group" variants={blockV} initial={false} animate="show">
              {/* Blok başlığı ve açıklaması KALDIRILDI (kullanıcı kararı,
                  12 Ağu 2026): bölüm başlığı zaten "modu siz seçersiniz"
                  diyor, hemen altında ikinci bir başlık aynı şeyi bir kez
                  daha söylüyordu. Anahtar doğrudan geliyor. */}
              <ControlModes />
            </motion.div>

            {/* ---------- 2–4) Sınırlar · Denetim kaydı · Erişim ---------- */}
            <ControlTabs />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===========================================================================
   Sekmeler — bölümdeki İKİ sekmeli blok da bunu kullanıyor
   ===========================================================================
   Örüntü tek bir yerde duruyor: gerçek `tablist` / `tab` rolleri, ok tuşları
   (←/→), Home/End, gezinen tabindex (yalnızca etkin sekme Tab sırasında),
   `aria-selected`, `aria-controls`. İki blokta iki ayrı uygulama olsaydı
   klavye davranışı zamanla ayrışırdı.

   İki DERİ var, ikisi de sayfada zaten konuşulan bir dil:

   • `strip` — koyu kutunun içindeki şerit; etkin sekmenin altındaki çizgi
     sekmeden sekmeye KAYIYOR (`layoutId`). Dar ekranda şerit KENDİ kabında
     yatay kayıyor, sayfa gövdesi asla (DESIGN.md §2).
   • `pill`  — fiyatlandırmadaki ödeme dönemi anahtarının aynısı (kullanıcı
     kararı, 12 Ağu 2026): gri kapsül, altında kayan beyaz kapak. Sayfada
     "iki seçenekten biri" hep bu nesneyle soruluyor.

   İkisinde de kayan parça `spring.snap`: küçük, anlık, elin altındaki şey. */

type StripItem = { key: string; icon?: RemixiconComponentType; label: string };

function TabStrip({
  items,
  active,
  onChange,
  label,
  uid,
  variant,
}: {
  items: readonly StripItem[];
  active: string;
  onChange: (key: string) => void;
  label: string;
  /** Sekme ve panel kimliklerinin ortak kökü; kayan çizginin `layoutId`'si de bundan. */
  uid: string;
  variant: 'strip' | 'pill';
}) {
  const btns = useRef<Record<string, HTMLButtonElement | null>>({});

  /* Ok tuşlarında seçim ANINDA değişiyor (otomatik etkinleştirme): panel
     içeriği yerelde, yükleme yok — WAI-ARIA bu durumda otomatiği öneriyor.
     Odak da taşınıyor, yoksa bir sonraki ok tuşu eski sekmeden hesaplanırdı. */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const keys = items.map((item) => item.key);
    const i = keys.indexOf(active);
    const next =
      event.key === 'ArrowRight'
        ? keys[(i + 1) % keys.length]
        : event.key === 'ArrowLeft'
          ? keys[(i - 1 + keys.length) % keys.length]
          : event.key === 'Home'
            ? keys[0]
            : event.key === 'End'
              ? keys[keys.length - 1]
              : null;

    if (!next) return;
    event.preventDefault();
    onChange(next);
    btns.current[next]?.focus();
  };

  /** İki deride de aynı: rol, kimlik, gezinen tabindex. */
  const tabProps = (key: string) => ({
    ref: (el: HTMLButtonElement | null) => {
      btns.current[key] = el;
    },
    type: 'button' as const,
    role: 'tab',
    id: `${uid}-tab-${key}`,
    'data-active': key === active,
    'aria-selected': key === active,
    'aria-controls': `${uid}-panel`,
    tabIndex: key === active ? 0 : -1,
    onClick: () => onChange(key),
  });

  if (variant === 'pill') {
    const i = items.findIndex((item) => item.key === active);

    return (
      <motion.div className="control-modes-switch-wrap" variants={copyV}>
        <div
          className="control-modes-switch"
          role="tablist"
          aria-label={label}
          onKeyDown={onKeyDown}
          /* 🔴 Kapak katmanının SÜTUN SAYISI buradan geliyor. Katman tek bir
             çocuk (kapak) taşıyor; sütun sayısı verilmezse o tek çocuk
             katmanın TAMAMINI kaplıyor ve `x: 100%` onu kapsülün dışına
             atıyor (ölçüldü, 12 Ağu 2026). */
          style={{ '--tab-count': items.length } as CSSProperties}
        >
          {/* Kapak kendi katmanında, etiketlerin ALTINDA: tıklamayı yemiyor.
              Yol yüzdeyle — kapak bir segment genişliğinde olduğu için
              `x: 100%` tam bir segment eder, ölçüm gerekmiyor. */}
          <span className="control-modes-thumb-layer" aria-hidden="true">
            <motion.span
              className="control-modes-thumb"
              initial={false}
              animate={{ x: `${i * 100}%` }}
              transition={spring.snap}
            />
          </span>

          {items.map(({ key, label: text }) => (
            <button key={key} className="control-modes-tab" {...tabProps(key)}>
              {text}
            </button>
          ))}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div className="control-tabs-scroll" variants={copyV}>
      <div className="control-tablist" role="tablist" aria-label={label} onKeyDown={onKeyDown}>
        {items.map(({ key, icon: Icon, label: text }) => {
          const on = key === active;

          return (
            <button key={key} className="control-tab" {...tabProps(key)}>
              {Icon ? <Icon size={18} aria-hidden="true" /> : null}
              {text}
              {on ? (
                <motion.span
                  className="control-tab-mark"
                  layoutId={`${uid}-mark`}
                  transition={spring.snap}
                  aria-hidden="true"
                />
              ) : null}
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}

/* ===========================================================================
   1) İki mod — ortalı hap anahtar + solda akış, sağda oynayan sahne
   ===========================================================================
   Eskiden iki durağan kart yan yanaydı. Şimdi tek bir öneri kartı var ve
   sekme onun SONUNU değiştiriyor: aynı kampanya, aynı bütçe önerisi, aynı
   gerekçe — ayrışan yalnızca son satır. Kart sekme değişince yeniden
   kurulmuyor, dolayısıyla gözün ölçtüğü şey tam olarak fark.

   ── Sahne bir kez oynuyor ─────────────────────────────────────────────────
   Sahne göründüğünde (useInView, once) bir zaman çizgisi başlıyor:

     adım 0  ajan hesaplıyor        → kartta yalnız kampanya satırı
     adım 1  öneri belli            → 400 üstü çiziliyor, 480 yerine oturuyor
     adım 2  kartın sonu            → onay modunda "bekleniyor" + düğmeler
                                       autopilotta doğrudan "uygulandı"
     adım 3  (yalnız onay modu)     → imleç "Onayla"ya iniyor ve basıyor
     adım 4  (yalnız onay modu)     → kart "uygulandı"ya dönüyor

   DÖNGÜ YOK: sahne bitince duruyor. Tekrarı isteyen sekmeye basıyor — zaman
   çizgisi her mod değişiminde baştan koşuyor. Kendi kendine tekrar eden bir
   maket bu bölümde huzursuzluk yaratırdı (DESIGN.md §4, "sakinlik lükstür").

   Azaltılmış hareket tercihinde zaman çizgisi HİÇ koşmuyor: sahne doğrudan
   son adımda basılıyor, bilgi kaybolmuyor. Karar `useEffect` içinde veriliyor
   (koşullu render DEĞİL) — `useReducedMotion` sunucuda ve tarayıcıda farklı
   ağaç üretip hidrasyonu kırıyor, bu depoda yaşanmış tuzak.

   ── Kartın boyu sabit ─────────────────────────────────────────────────────
   Kartın son satırı üç ayrı hâle giriyor (hesaplıyor / bekliyor / uygulandı)
   ve üçünün doğal boyu farklı. `layout` ile boy animasyonlamak yerine alt
   satıra en uzun hâlin boyu kadar YER AYRILDI (CSS → .control-canvas-foot):
   kart hiç kımıldamıyor, içerik onun içinde değişiyor. Aynı karar bölümün
   alt sekmeli kartında da alınmıştı, gerekçesi orada uzun uzun yazılı.

   ── Sahne ekran okuyucuya KAPALI ──────────────────────────────────────────
   `aria-hidden`: sahne solda yazılı olanın bir yeniden anlatımı ve her adımda
   değişiyor — açık bırakılsaydı okuyucu, kullanıcı hiçbir şey yapmadan üç kez
   konuşurdu. Anlamı taşıyan metin solda: başlık, açıklama ve üç adımlık akış.
   =========================================================================== */

/* İkon YOK ve bu bilinçli: hap anahtar fiyatlandırmadaki kardeşiyle aynı
   olmalı, orada da yalnızca metin var. İki seçenekli bir anahtarda ikon
   kapsülü şişiriyor ve etiketleri birbirinden uzaklaştırıyor. */
const MODES = ['ask', 'auto'] as const;

type ModeKey = (typeof MODES)[number];

/** Her adımın sahne başlangıcından kaç MİLİSANİYE sonra geldiği. */
const SCENE: Record<ModeKey, readonly number[]> = {
  ask: [0, 800, 1500, 2200, 2600],
  auto: [0, 800, 1500],
};

/** Soldaki akış: üç satır, sahnedeki ilk üç adımla birebir. */
const FLOW = ['s1', 's2', 's3'] as const;

/* Kartın son satırı. Giriş yerine oturuyor, çıkış kısa kesiliyor: bekleyen bir
   geçişte (mode="wait") uzun çıkış, iki hâl arasına ölü zaman koyar. */
const footV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
  exit: { opacity: 0, y: -6, transition: { duration: dur.instant, ease: ease.in } },
};

/* İmleç düğmenin KENDİ içinde konumlanıyor (mutlak), ekran koordinatıyla
   değil: düğme nereye giderse imleç de oraya gider, kırılma noktası gerekmiyor.
   Yol sağ alttan geliyor — ekranda elin geldiği yön. */
const cursorV: Variants = {
  off: { opacity: 0, x: 30, y: 24, scale: 1 },
  aim: {
    opacity: 1,
    x: 0,
    y: 0,
    scale: 1,
    transition: {
      ...spring.default,
      delay: 0.18,
      opacity: { duration: dur.fast, ease: ease.out, delay: 0.18 },
    },
  },
  press: { opacity: 1, x: 0, y: 0, scale: 0.84, transition: spring.snap },
};

function ControlModes() {
  const t = useTranslations('control.modes');
  /* İki nokta üst üste CSS seçicisinde ve `layoutId`'de sorun çıkarıyor. */
  const uid = useId().replace(/:/g, '');
  const [mode, setMode] = useState<ModeKey>('ask');
  const [step, setStep] = useState(0);
  const stage = useRef<HTMLDivElement>(null);
  /* Sahne ekranda görünmeden oynamasın: kullanıcı bölüme geldiğinde başlıyor. */
  const seen = useInView(stage, { once: true, amount: 0.35 });
  const reduce = useReducedMotion();

  useEffect(() => {
    const marks = SCENE[mode];

    if (!seen) {
      setStep(0);
      return;
    }
    if (reduce) {
      setStep(marks.length - 1);
      return;
    }

    setStep(0);
    const timers = marks.slice(1).map((ms, i) => window.setTimeout(() => setStep(i + 1), ms));

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [mode, seen, reduce]);

  const marks = SCENE[mode];
  /** Yeni bütçe hesaplandı. */
  const priced = step >= 1;
  /** Kartın sonu belli oldu. */
  const settled = step >= 2;
  /** Zaman çizgisi bitti. */
  const ended = step >= marks.length - 1;
  const pressed = mode === 'ask' && step >= 3;
  const foot = !settled ? 'calc' : mode === 'ask' && !ended ? 'wait' : 'done';
  /** Soldaki akışta o an hangi satırda olduğumuz. */
  const at = Math.min(step, FLOW.length - 1);

  return (
    <motion.div className="control-modes" variants={blockV}>
      <TabStrip
        items={MODES.map((key) => ({ key, label: t(`${key}.tab`) }))}
        active={mode}
        onChange={(key) => setMode(key as ModeKey)}
        label={t('tablist')}
        uid={uid}
        variant="pill"
      />

      <motion.div
        className="control-modes-panel"
        variants={surfaceV}
        role="tabpanel"
        id={`${uid}-panel`}
        aria-labelledby={`${uid}-tab-${mode}`}
        tabIndex={0}
      >
        {/* ---------- Solda: modun kendisi ---------- */}
        <div className="control-modes-copy">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={mode}
              className="control-modes-body"
              variants={panelCopyV}
              initial="hidden"
              animate="show"
              exit="exit"
            >
              <motion.h4 className="control-mode-title" variants={partV}>
                {t(`${mode}.title`)}
              </motion.h4>
              <motion.p className="control-mode-text" variants={partV}>
                {t(`${mode}.desc`)}
              </motion.p>

              {/* Akış sahneyle AYNI zaman çizgisinde ilerliyor: sağda olan şey
                  solda yazılı. Numara rozeti yok — sıra zaten satırın kendisi. */}
              <motion.ol className="control-flow" variants={partsV}>
                {FLOW.map((key, i) => {
                  const done = i < at || (i === at && ended);
                  const live = i === at && !ended;

                  return (
                    <motion.li
                      key={key}
                      className="control-flow-step"
                      data-state={done ? 'done' : live ? 'live' : 'idle'}
                      variants={partV}
                    >
                      <span className="control-flow-mark" aria-hidden="true">
                        <motion.span
                          className="control-flow-dot"
                          initial={{ opacity: 1, scale: 0.7 }}
                          animate={{ opacity: done ? 0 : 1, scale: live ? 1 : 0.7 }}
                          transition={spring.snap}
                        />
                        <motion.span
                          className="control-flow-tick"
                          initial={{ opacity: 0, scale: 0.5 }}
                          animate={{ opacity: done ? 1 : 0, scale: done ? 1 : 0.5 }}
                          transition={spring.bouncy}
                        >
                          <RiCheckLine size={14} aria-hidden="true" />
                        </motion.span>
                      </span>
                      <span className="control-flow-text">{t(`${mode}.flow.${key}`)}</span>
                    </motion.li>
                  );
                })}
              </motion.ol>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ---------- Sağda: sahne ---------- */}
        <div className="control-canvas" ref={stage} aria-hidden="true">
          <span className="control-canvas-grid" />
          <span className="control-canvas-glow" />

          <div className="control-surface control-card control-canvas-card">
            <div className="control-card-head">
              <RiRobot2Line size={16} aria-hidden="true" />
              <span className="control-card-agent">{t('proposal.agent')}</span>
              <span
                className={`control-mode-flag${mode === 'auto' ? ' control-mode-flag--on' : ''}`}
              >
                {/* Anahtar sekmeyle birlikte KAYIYOR: mod bir ayar, sekme o
                    ayarın kendisi. Yol 11px — CSS'teki ölçülere bağlı
                    (26 genişlik − 2×2 dolgu − 11 topuz). */}
                <span
                  className={`control-switch${mode === 'auto' ? ' control-switch--on' : ''}`}
                  aria-hidden="true"
                >
                  <motion.span
                    className="control-switch-knob"
                    initial={{ x: 0 }}
                    animate={{ x: mode === 'auto' ? 11 : 0 }}
                    transition={spring.snap}
                  />
                </span>
                {t(`${mode}.badge`)}
              </span>
            </div>

            <p className="control-card-campaign">{t('proposal.campaign')}</p>

            <p className="control-card-budget">
              <span className="control-card-from">
                {t('proposal.from')}
                {/* Üstü çizgi CSS'in `line-through`u değil: çizilme ANI
                    okunuyor, çizgi soldan sağa geçiyor. */}
                <motion.span
                  className="control-strike"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: priced ? 1 : 0 }}
                  transition={{ duration: dur.fast, ease: ease.out }}
                />
              </span>
              <motion.span
                className="control-card-arrow"
                initial={{ opacity: 0 }}
                animate={{ opacity: priced ? 1 : 0 }}
                transition={{ duration: dur.fast, ease: ease.out }}
              >
                →
              </motion.span>
              <motion.span
                className="control-card-to"
                initial={{ opacity: 0, y: 6 }}
                animate={priced ? { opacity: 1, y: 0 } : { opacity: 0, y: 6 }}
                transition={springFade(spring.default)}
              >
                {t('proposal.to')}
              </motion.span>
              <motion.span
                className="control-card-delta"
                initial={{ opacity: 0, y: 6 }}
                animate={priced ? { opacity: 1, y: 0 } : { opacity: 0, y: 6 }}
                transition={{ ...springFade(spring.default), delay: 0.06 }}
              >
                {t('proposal.delta')}
              </motion.span>
            </p>

            <motion.p
              className="control-card-reason"
              initial={{ opacity: 0 }}
              animate={{ opacity: priced ? 1 : 0 }}
              transition={{ duration: dur.fast, ease: ease.out }}
            >
              <span className="control-card-reason-label">{t('proposal.reasonLabel')}</span>
              {t('proposal.reason')}
            </motion.p>

            {/* Boyu SABİT: en uzun hâl (bekleyen onay) kadar yer ayrıldı. */}
            <div className="control-card-foot control-canvas-foot">
              <AnimatePresence mode="wait" initial={false}>
                {foot === 'calc' ? (
                  <motion.div
                    key="calc"
                    variants={footV}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                  >
                    <p className="control-state control-state--calc">
                      {/* Nabız CSS'te: sonsuz döngü, azaltılmış harekette
                          kapanıyor (globals.css sonundaki blok). */}
                      <span className="control-calc-dot" />
                      {t('calc')}
                    </p>
                  </motion.div>
                ) : foot === 'wait' ? (
                  <motion.div
                    key="wait"
                    variants={footV}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                  >
                    <p className="control-state">
                      <RiTimeLine size={15} aria-hidden="true" />
                      {t('ask.state')}
                    </p>
                    {/* Gerçek düğme değil — bileşen başındaki nota bak. */}
                    <p className="control-actions">
                      <span className="control-btn control-btn--ghost">{t('ask.reject')}</span>
                      <motion.span
                        className="control-btn control-btn--primary control-approve"
                        animate={{ scale: pressed ? 0.96 : 1 }}
                        transition={spring.snap}
                      >
                        {t('ask.approve')}
                        <motion.span
                          className="control-press-ring"
                          initial={{ opacity: 0, scale: 0.7 }}
                          animate={
                            pressed
                              ? { opacity: [0, 0.45, 0], scale: 1.45 }
                              : { opacity: 0, scale: 0.7 }
                          }
                          transition={{ duration: dur.base, ease: ease.out }}
                        />
                        <motion.span
                          className="control-cursor"
                          variants={cursorV}
                          initial="off"
                          animate={pressed ? 'press' : 'aim'}
                        >
                          <RiCursorFill size={15} aria-hidden="true" />
                        </motion.span>
                      </motion.span>
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="done"
                    variants={footV}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                  >
                    <p className="control-state control-state--done">
                      <motion.span
                        className="control-state-tick"
                        initial={{ scale: 0.4, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={spring.bouncy}
                      >
                        <RiCheckLine size={15} aria-hidden="true" />
                      </motion.span>
                      {mode === 'ask' ? t('ask.applied') : t('auto.state')}
                    </p>
                    <p className="control-state-note">
                      {mode === 'ask' ? t('ask.appliedNote') : t('auto.note')}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ===========================================================================
   Sınırlar · Denetim kaydı · Erişim — sekmeli kart
   ===========================================================================
   Üç madde eskiden alt alta üç satırdı; her birinin yanında elle çizilmiş bir
   maket duruyordu. 12 Ağu 2026'da (kullanıcı kararı) üçü tek bir sekmeli
   karta indi: şerit üstte, kart altında, kartın içi ikiye bölünmüş — solda
   metin, sağda ürünün kendi ekran görüntüsü.

   ── Neden sekme ───────────────────────────────────────────────────────────
   Üç maddenin üçü de aynı soruyu üç ayrı yerden cevaplıyor ("ajan neye kadar
   yetkili"). Alt alta üç satırda bölüm uzuyor ve okuyucu üçünü de okumak
   zorunda hissediyordu; sekmede hangisini merak ediyorsa ona bakıyor. Sıra
   yine anlamlı: önce sınır (ne kadar), sonra kayıt (ne oldu), sonra erişim
   (nereye kadar).

   ── Görsel HENÜZ YOK ──────────────────────────────────────────────────────
   `TAB_SHOT` üç anahtarda da `null`; o hâlde panel sessiz bir yer tutucu
   (ızgara doku + bir görsel işareti, metin yok — "yakında" gibi bir cümle
   ziyaretçiye eksikliği duyurur). Geldiğinde yapılacak tek şey yolu yazmak:

       const TAB_SHOT: Record<TabKey, string | null> = {
         limits: '/shots/limits.png',
         ...

   Dosyalar `public/shots/` altına konur. Yer tutucu ile görselin kapladığı
   alan aynı, dolayısıyla dosya eklenince düzen kımıldamıyor. Panelin oranı
   sabit değil (geniş ekranda boyu karttan geliyor) — görsel `object-fit:
   cover` ile kırpılıyor, yatay ve ortası kalabalık bir kompozisyon seçin.

   ── Erişilebilirlik ───────────────────────────────────────────────────────
   Gerçek `tablist` / `tab` / `tabpanel`: rol bir söz veriyor ve burada o söz
   tutuluyor — ok tuşları (←/→), Home/End, gezinen tabindex (yalnızca etkin
   sekme Tab sırasında), `aria-selected`, `aria-controls`. Panelin kendisi
   odaklanabilir (`tabIndex={0}`): içinde odaklanacak bir öğe yok, klavyeyle
   gezen kullanıcı metne başka türlü ulaşamazdı.

   Şerit dar ekranda kendi kabında yatay kayıyor; sayfa gövdesi asla
   (DESIGN.md §2).

   ── Hareket ───────────────────────────────────────────────────────────────
   • Etkin sekmenin altındaki çizgi sekmeden sekmeye KAYIYOR (`layoutId`) —
     ışınlanan bir vurgu "yeni bir şey belirdi" der, kayan vurgu "aynı şey,
     başka değer". Panelin geri kalanıyla aynı cümle.
   • Metin `mode="wait"` ile değişiyor: iki farklı metin bir an üst üste
     binerse ikisi de okunmaz hâle gelir.
   • Görsel ise ÜST ÜSTE geçiyor (mutlak konumlu, `mode` yok): iki görüntü
     birbirine karışmaz, çapraz geçiş burada doğru jest.

   🔴 Kartın BOYU animasyonlanmıyor (`layout` prop'u YOK) ve bu bilinçli:
   - Geniş ekranda zaten değişmiyor; kartın `min-height`'ı üç metnin en
     uzununu da alıyor, sekme değiştirmek kartı kımıldatmıyor.
   - Dar ekranda metin alt alta olduğu için boy değişiyor. Orada `layout`
     denendi ve iki yoldan da bozuluyor: projeksiyon, `layout` taşımayan
     çocukları ölçekliyor (yer tutucunun ızgara dokusu eziliyor), çocuklara da
     verilince `mode="wait"`in boşluğunda kart önce çöküp sonra açılıyor —
     bir "pompalama". Boy şimdi ANİ değişiyor ama tam da eski metnin
     silindiği, yeninin henüz gelmediği anda: gözün baktığı yer o sırada boş.
   =========================================================================== */

const TABS = ['limits', 'log', 'access'] as const;
type TabKey = (typeof TABS)[number];

const TAB_ICON = {
  limits: RiEqualizerLine,
  log: RiFileList3Line,
  access: RiKey2Line,
} as const;

/** Her sekmenin görseli. `null` iken yer tutucu çiziliyor (yukarıdaki nota bak). */
const TAB_SHOT: Record<TabKey, string | null> = {
  limits: null,
  log: null,
  access: null,
};

/* Panel metni: girerken yerine oturuyor, çıkarken kısa kesiliyor — bekleyen
   bir geçişte (mode="wait") uzun bir çıkış, tıklama ile yeni metin arasına
   ölü zaman koyar. */
const panelCopyV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: {
    opacity: 1,
    y: 0,
    transition: { ...springFade(spring.default), staggerChildren: stagger.base },
  },
  exit: { opacity: 0, y: -6, transition: { duration: dur.instant, ease: ease.in } },
};

/* Görselin çapraz geçişi. Ölçek 1.03'ten 1'e: yeni görüntü "yerleşiyor",
   yalnız opaklıkla gelen bir görsel ise yerinde beliriyor gibi duruyordu. */
const panelShotV: Variants = {
  hidden: { opacity: 0, scale: 1.03 },
  show: { opacity: 1, scale: 1, transition: springFade(spring.default, dur.base) },
  exit: { opacity: 0, transition: { duration: dur.fast, ease: ease.in } },
};

function ControlTabs() {
  const t = useTranslations('control');
  /* `useId` iki kez kullanılıyor: sekme ↔ panel bağı ve kayan çizginin
     `layoutId`'si. Kimlik örnek başına üretiliyor — aynı sayfada iki şerit
     olsaydı tek bir çizgi ikisi arasında uçardı. İki nokta üst üste CSS
     seçicisinde geçerli değil, temizleniyor. */
  const uid = useId().replace(/:/g, '');
  const [active, setActive] = useState<TabKey>('limits');

  const shot = TAB_SHOT[active];
  const ActiveIcon = TAB_ICON[active];

  return (
    <motion.div className="control-tabs" variants={blockV} initial={false} animate="show">
      <TabStrip
        items={TABS.map((key) => ({ key, icon: TAB_ICON[key], label: t(`${key}.tab`) }))}
        active={active}
        onChange={(key) => setActive(key as TabKey)}
        label={t('tablist')}
        uid={uid}
        variant="strip"
      />

      <motion.div
        className="control-panel"
        variants={surfaceV}
        role="tabpanel"
        id={`${uid}-panel`}
        aria-labelledby={`${uid}-tab-${active}`}
        tabIndex={0}
      >
        <div className="control-panel-copy">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={active}
              className="control-panel-body"
              variants={panelCopyV}
              initial="hidden"
              animate="show"
              exit="exit"
            >
              {/* İkon kutusuz, doğrudan yüzeyde (DESIGN.md §3) ve sekmedekiyle
                  aynı: hangi sekmede olduğunuz panelin içinde de yazıyor. */}
              <motion.span className="control-panel-icon" variants={partV}>
                <ActiveIcon size={24} aria-hidden="true" />
              </motion.span>
              <motion.h3 className="control-h3" variants={partV}>
                {t(`${active}.title`)}
              </motion.h3>
              <motion.p className="control-text" variants={partV}>
                {t(`${active}.desc`)}
              </motion.p>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="control-panel-shot">
          <AnimatePresence initial={false}>
            {shot ? (
              <motion.img
                key={active}
                className="control-shot-img"
                src={shot}
                alt={t(`${active}.shotAlt`)}
                loading="lazy"
                decoding="async"
                variants={panelShotV}
                initial="hidden"
                animate="show"
                exit="exit"
              />
            ) : (
              /* Yer tutucu ekran okuyucudan gizli: okunacak bir bilgi yok. */
              <motion.span
                key={active}
                className="control-shot-empty"
                variants={panelShotV}
                initial="hidden"
                animate="show"
                exit="exit"
                aria-hidden="true"
              >
                <RiImageLine size={30} aria-hidden="true" />
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
}
